import useDeleteWorkspace from "api/api-hooks/workspaces/useDeleteWorkspace";
import { useEffect, useState } from "react";
import { Button } from "reactstrap";
import useWorkspace from "api/api-hooks/workspaces/useWorkspace";
import { useAtomValue } from "jotai";
import { newToastEvent } from "utils/ToastUtil";
import useUpdateWorkspaceSettings from "api/api-hooks/workspaces/useUpdateWorkspaceSettings";
import useWorkspacePermissions from "hooks/useWorkspacePermissions";
import CollapsibleGroup from "views/cloud/CreateCloudView/CreateCloudComponents/CollapsibleGroup";
import WorkspaceInfoSection from "components/Drawers/EditWorkspaceDrawer/WorkspaceInfoSection";
import WorkspaceFiltersSection from "components/Drawers/EditWorkspaceDrawer/WorkspaceFiltersSection";
import WorkspacePrefillsSection from "components/Drawers/EditWorkspaceDrawer/WorkspacePrefillsSection";
import { tenantIDAtom } from "atoms/GlobalAtoms";
import _ from "underscore";
import AppSpinner from "../../elements/AppSpinner";
import { useNavigate } from "react-router-dom";
import Constants from "../../utils/Constants";
import AppEmptyState from "../../elements/AppEmptyState";
import useCloudPermissions from "../../hooks/useCloudPermissions";

const WorkspaceSettings = () => {
  const urlParams = new URLSearchParams(window.location.search);
  const { workspace, workspaceData } = useWorkspace();
  const { updateSettings, updateSubmitting } = useUpdateWorkspaceSettings();
  const { deleteWorkspace, deleteSubmitting } = useDeleteWorkspace();
  const { wPermissions } = useWorkspacePermissions();
  const { cPermissions } = useCloudPermissions();
  const navigate = useNavigate();
  const tenantID = useAtomValue(tenantIDAtom);

  const hasPermissions =
    wPermissions?.workspace_modify_workspace_settings ||
    cPermissions?.cloud_modify_workspace;

  const isSupportal = tenantID === Constants.SUP_CLOUD_ID;

  const [values, setValues] = useState(() => {
    const workspaceObject = structuredClone(workspace);

    if (!!workspace?.Prefills === false) {
      workspaceObject.Prefills = [];
    }

    if (!!workspace?.Filters === false) {
      workspaceObject.Filters = [];
    }

    return workspaceObject;
  });

  const onChange = (e) => {
    setValues({ ...values, [e.target.name]: e.target.value });
  };

  useEffect(() => {
    if (!hasPermissions) {
      newToastEvent("error", "You don't have permission to view that page.");
      navigate(`/clouds/${tenantID}/workspaces/${workspace?.id}/home/pages`);
    }
  }, [navigate, hasPermissions, tenantID, workspace?.id]);

  const saveSettings = () => {
    const valuesCopy = { ...values };
    const payload = {};

    if (valuesCopy?.Name !== workspace?.Name) {
      payload.Name = valuesCopy?.Name;
    }
    if (valuesCopy?.Description !== workspace?.Description) {
      payload.Description = valuesCopy?.Description;
    }
    if (valuesCopy?.Privacy !== workspace?.Privacy) {
      payload.Privacy = valuesCopy?.Privacy;
    }

    // Handle Filters
    if (!workspace.hasOwnProperty("Filters") && valuesCopy.Filters.length > 0) {
      payload.Filters = valuesCopy.Filters;
    }
    if (
      workspace.hasOwnProperty("Filters") &&
      !_.isEqual(valuesCopy.Filters, workspace.Filters)
    ) {
      payload.Filters = valuesCopy.Filters;
    }

    // Handle Prefills
    if (
      !workspace.hasOwnProperty("Prefills") &&
      valuesCopy.Prefills.length > 0
    ) {
      payload.Prefills = valuesCopy.Prefills;
    }
    if (
      workspace.hasOwnProperty("Prefills") &&
      !_.isEqual(valuesCopy.Prefills, workspace.Prefills)
    ) {
      payload.Prefills = valuesCopy.Prefills;
    }

    updateSettings({
      workspaceID: workspace.id,
      payload: payload,
      onSuccess: () => newToastEvent("success", "Settings saved"),
    });
  };

  const handleDeleteWorkspace = () => {
    deleteWorkspace(workspace.id);
  };

  const SaveButton = ({ css }) => {
    let compareValues = { ...values };

    if (
      !workspace.hasOwnProperty("Filters") &&
      compareValues?.Filters?.length === 0
    ) {
      delete compareValues.Filters;
    }
    if (
      !workspace.hasOwnProperty("Prefills") &&
      compareValues?.Prefills?.length === 0
    ) {
      delete compareValues.Prefills;
    }

    return (
      <Button
        color="primary"
        onClick={saveSettings}
        className={css}
        disabled={
          updateSubmitting ||
          !wPermissions.workspace_modify_workspace_settings ||
          _.isEqual(compareValues, workspace)
        }
        style={{ width: 180 }}
      >
        {updateSubmitting ? (
          <>
            Saving...
            <AppSpinner className="ml-3" size="sm" color="white" />
          </>
        ) : (
          "Save Settings"
        )}
      </Button>
    );
  };

  if (tenantID === Constants.SUP_CLOUD_ID) {
    return (
      <AppEmptyState
        title="Settings not available"
        description="Settings are not available for this workspace."
      />
    );
  }

  return (
    <>
      <CollapsibleGroup
        title="Workspace Information"
        description="Basic information about the workspace"
        startOpen={false}
      >
        <WorkspaceInfoSection values={values} onChange={onChange} />
        <SaveButton />
      </CollapsibleGroup>
      <CollapsibleGroup
        title="Default Filters"
        description="Manage workspace data source filters"
        startOpen={false}
      >
        <WorkspaceFiltersSection
          values={values}
          setValues={setValues}
          useHeader={false}
        />
        <SaveButton css="mt-4" />
      </CollapsibleGroup>
      <CollapsibleGroup
        title="Default Values"
        description="Enforce default values on record creation"
        startOpen={false}
      >
        <WorkspacePrefillsSection
          values={values}
          setValues={setValues}
          useHeader={false}
        />
        <SaveButton css="mt-4" />
      </CollapsibleGroup>
      <CollapsibleGroup
        title="Delete Workspace"
        description="Delete workspace and all of its pages."
        startOpen={!!urlParams?.get("delete")}
      >
        <Button
          color="danger"
          onClick={handleDeleteWorkspace}
          disabled={!workspaceData.isOwner || deleteSubmitting || isSupportal}
        >
          {deleteSubmitting ? (
            <>
              Deleting...
              <AppSpinner className="ml-3" size="sm" />
            </>
          ) : (
            "Delete Workspace"
          )}
        </Button>
      </CollapsibleGroup>
    </>
  );
};

export default WorkspaceSettings;
